import { createFileRoute } from "@tanstack/react-router";
import { PillarPage, buildPillarHead } from "@/components/site/PillarPage";
import { PILLAR_BY_SLUG } from "@/lib/pillars";

const pillar = PILLAR_BY_SLUG["marketing-strategy"];

const faqs = [
  {
    q: "Do I have to create my own marketing content?",
    a: "No. KCN provides done-for-you marketing assets - listing alerts, digital graphics, market reports, video frameworks - plus admin support to deploy them. You focus on conversations, not Canva.",
  },
  {
    q: "What kind of strategy support is included?",
    a: "Strategy isn't a deck - it's a cadence. KCN agents get a marketing calendar tied to listings, market reports, and seasonal moves, with coaching on what to post, when, and what to expect from it.",
  },
  {
    q: "Do I get my own website and blog content?",
    a: "Yes. KCN gives you an AI-powered agent site with consistent weekly blog articles published in your name. You do not write them and you do not manage the site. The content targets what buyers and sellers in your market are actually searching for, so your visibility builds week over week.",
  },
  {
    q: "Why does KCN push organic marketing over paid leads?",
    a: "Paid leads are rent. The moment you stop paying, the pipeline stops. Organic visibility is an asset you own - the content, reviews, and search presence you build keep producing higher-intent leads long after the work is done. KCN invests agents in the side that compounds.",
  },
  {
    q: "Will my marketing look like everyone else's at KCN?",
    a: "The frameworks are shared; the voice stays yours. Agents own their personal brand inside the KCN system - we provide the engine, not a uniform.",
  },
];

export const Route = createFileRoute("/marketing-content-and-strategy")({
  head: () => buildPillarHead(pillar, faqs),
  component: () => (
    <PillarPage
      pillar={pillar}
      intro="Most agents either over-produce content nobody sees, or under-produce and disappear from the feed. KCN gives you the assets, the calendar, and the strategy - so marketing becomes a system instead of a side job."
      sections={[
        {
          eyebrow: "The content engine",
          heading: "Done-for-you assets, deployed on cadence.",
          bullets: [
            "Listing Alerts - timely updates that keep agents top-of-mind",
            "Digital Graphics - branded creative for social and outreach",
            "Market Reports - local data that positions you as the expert",
            "Video Support - frameworks and production help for short-form video",
            "AI-Powered Agent Site - weekly blog articles published in your name to drive organic visibility",
          ],
        },
        {
          eyebrow: "The strategy layer",
          heading: "A marketing calendar, not a wishlist.",
          body: "Content without strategy is noise. KCN agents get a marketing cadence tied to listings, market shifts, and seasonal opportunities - coached by people who actually use it to win business. You'll know what to post, when, and what each piece is supposed to do.",
        },
        {
          eyebrow: "Rent vs. own",
          heading: "Paid attention expires. Organic visibility compounds.",
          body: "Most agents rent their attention. They buy leads and run ads, and the day the card stops, so does the pipeline. Organic marketing works the other way: the content, reviews, and search presence you build this year keep bringing in higher-intent leads next year, for free. Most agents never invest here because they do not know how - which is exactly the gap KCN closes for you.",
          bullets: [
            "Paid: rented attention, resets to zero when spend stops",
            "Organic: owned visibility that pays dividends for years",
            "Higher intent - people searching for you already want to buy or sell",
            "We build it with you, so it is not one more thing on your list",
          ],
        },
        {
          eyebrow: "Your site, your name",
          heading: "An AI-powered site publishing in your name every week.",
          body: "Every KCN agent gets an AI-based website with fresh blog articles published weekly under their own byline. You do not write them, schedule them, or maintain the site. The topics are chosen from what buyers and sellers in your market are actually searching, so each article adds another way for the right person to find you.",
          bullets: [
            "AI-powered agent website, built and maintained for you",
            "Consistent weekly blog articles published in your name",
            "Topics driven by real local search demand",
            "Every post becomes a permanent asset working for you",
          ],
        },

        {
          eyebrow: "Authority compounding",
          heading: "Become the expert your market searches for.",
          body: "Consistent market reports, neighborhood content, and video position you as the obvious local authority. That's how leads start coming inbound - and how referrals start citing you by name.",
        },
      ]}
      benefits={{
        heading: "What KCN marketing gives you.",
        items: [
          "Done-for-you listing alerts",
          "Branded digital graphics",
          "Local market reports on cadence",
          "Video frameworks and production support",
          "Marketing strategy coaching",
          "An organic visibility presence you own",
          "An AI-powered site with weekly blog content in your name",
          "Admin support to actually deploy assets",
        ],
      }}
      faqs={faqs}
    />
  ),
});
